import { toast } from "sonner";

import { moneefloApi } from "./client";
import { coreError } from "./core-error";
import { getLiveRates } from "./legacy-order-api";

/**
 * The "Create Forex Order" wizard (`/neworder?type=forex`), answered by the
 * new backend where it can be.
 *
 * What exists for staff today: finding a customer, adding one, and the
 * catalogue's countries. The rate and the GST calculation were already moved
 * (legacy-order-api.ts) and are not touched here. What does not exist: any way
 * for staff to place a multi-currency forex order, check or apply a coupon,
 * send a payment link, or read an identity document. The callers of those say
 * "not available yet" through notAvailable() in the apis file; nothing here
 * pretends otherwise.
 */

type CustomerRow = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  kycStatus: "verified" | "pending" | "not_started";
  panLast4: string | null;
};

export type ForexCustomer = {
  phone_number: string;
  full_name: string;
  email: string;
  pan_no: string;
  id: string;
  kyc_status?: string;
};

const toForexCustomer = (row: CustomerRow): ForexCustomer => ({
  id: row.id,
  full_name: row.fullName,
  phone_number: row.phone ?? "",
  email: row.email ?? "",
  // Only the last four digits ever leave the database. The mask is shown as
  // it arrives and is never turned back into a number on the client.
  pan_no: row.panLast4 ? `••••${row.panLast4}` : "",
  kyc_status: row.kycStatus,
});

/** Tell the operator once, then let the caller's own error path run. */
function surface(error: unknown, fallback: string, id: string): never {
  const failure = coreError(error, fallback);
  toast.error(failure.message, { id });
  throw failure;
}

/** Customers whose phone, name or email contains what was typed. */
export async function searchForexCustomers(term: string): Promise<ForexCustomer[]> {
  const search = term.trim();
  if (search.length < 4) return [];
  try {
    const { data } = await moneefloApi.get<{ items: CustomerRow[] }>("/customers", {
      params: { search, perPage: 10 },
    });
    return (data?.items ?? []).map(toForexCustomer);
  } catch (error) {
    return surface(error, "Customers could not be searched.", "forex-customer-search");
  }
}

/**
 * Adds the customer the wizard just collected.
 *
 * The PAN is saved as an identity once the customer exists. If that one step
 * is refused (for example the PAN is already on another customer) the customer
 * is kept and the operator is told, rather than the whole step being lost.
 */
export async function createForexCustomer(input: {
  name: string;
  pan_no: string;
  phone_number: string;
  email: string;
}): Promise<{ id: string; full_name: string }> {
  let created: { id: string; fullName: string };
  try {
    const { data } = await moneefloApi.post<{ id: string; fullName: string }>(
      "/customers",
      {
        fullName: input.name.trim(),
        phone: input.phone_number.trim() || undefined,
        email: input.email.trim() || undefined,
        source: "forex_order",
      },
    );
    created = data;
  } catch (error) {
    return surface(error, "The customer could not be added.", "forex-customer-create");
  }

  const pan = input.pan_no.trim().toUpperCase();
  // A masked value came from a search result: it is already on file.
  if (pan && !pan.includes("•")) {
    try {
      await moneefloApi.post(`/customers/${created.id}/identities`, {
        docType: "pan",
        value: pan,
        nameOnDoc: input.name.trim(),
      });
    } catch (error) {
      toast.warning(
        `${created.fullName} was added, but the PAN was not saved: ${coreError(error, "it was refused").message}`,
        { id: "forex-customer-pan" },
      );
    }
  }
  return { id: created.id, full_name: created.fullName };
}

/** The catalogue's countries, in the shape the destination step reads. */
export async function listForexCountries(): Promise<
  { name: { common: string; official: string } }[]
> {
  try {
    const { data } = await moneefloApi.get<{ name: string }[]>("/catalog/countries");
    return (data ?? []).map((country) => ({
      name: { common: country.name, official: country.name },
    }));
  } catch (error) {
    return surface(error, "Countries could not be loaded.", "forex-countries");
  }
}

/**
 * The live rate, for the wizard's product form. Its callers do not catch, so a
 * currency with no rate set left the rate field empty with no word on why.
 * The same call the dashboard and the add-currency dialog make, which already
 * report their own failures and are not changed.
 */
export async function liveRateForWizard(args: Parameters<typeof getLiveRates>[0]) {
  try {
    return await getLiveRates(args);
  } catch (error) {
    return surface(error, `The rate for ${args.currency} could not be loaded.`, `forex-rate-${args.currency}`);
  }
}

/* ------------------------------------------------ lists the wizard picks from */

export type Option = { value: string; label: string };

/** The catalogue's purposes. A sale carries the purpose id, never its name. */
export async function listForexPurposes(): Promise<Option[]> {
  try {
    const { data } = await moneefloApi.get<{ id: string; name: string }[]>("/catalog/purposes");
    return (data ?? []).map((p) => ({ value: p.id, label: p.name }));
  } catch (error) {
    return surface(error, "Purposes could not be loaded.", "forex-purposes");
  }
}

/** Companies that can be named as the payer of an employer-paid trip. */
export async function listForexCorporates(): Promise<Option[]> {
  try {
    const { data } = await moneefloApi.get<{ id: string; name: string; city: string | null }[]>(
      "/catalog/corporates",
    );
    return (data ?? []).map((c) => ({
      value: c.id,
      label: c.city ? `${c.name} (${c.city})` : c.name,
    }));
  } catch (error) {
    return surface(error, "Companies could not be loaded.", "forex-corporates");
  }
}

/**
 * Documents filed on the customer that could prove an employer pays. Core
 * marks each one `sponsorEvidenceEligible` (verified, and of a type in the
 * business's `forex.sponsor_evidence_doc_types` setting), and that is what is
 * filtered on; no type list is kept here. A document filed on the order can
 * never count as evidence, so only the customer's own are listed.
 */
export async function listCustomerDocumentOptions(customerId: string): Promise<Option[]> {
  try {
    const { data } = await moneefloApi.get<
      {
        id: string;
        docType: string;
        fileName: string | null;
        label: string | null;
        status: string;
        /** Core's own answer: verified, and of a type the business accepts as evidence. */
        sponsorEvidenceEligible?: boolean;
      }[]
    >(`/customers/${encodeURIComponent(customerId)}/documents`);
    return (data ?? [])
      // Until core sends its answer, verified is the part it already checks.
      .filter((d) => d.sponsorEvidenceEligible ?? d.status === "verified")
      .map((d) => ({
        value: d.id,
        label: d.label || d.fileName || d.docType,
      }));
  } catch (error) {
    return surface(error, "The customer's documents could not be loaded.", "forex-customer-documents");
  }
}

/* ------------------------------------------------------------ the order -- */

export type CreateForexOrderPayload = {
  customerId: string;
  direction: "sale" | "encashment";
  purposeId?: string;
  destinationCountry?: string;
  residency: "resident" | "non_resident" | "unknown";
  travellerCustomerId?: string;
  payer: { kind: "self" | "employer" | "sponsor" | "other"; organizationId?: string; name?: string };
  employerBorne: boolean;
  sponsorEvidenceDocumentId?: string;
  lrsDeclaredUsd?: number;
  lrsSigned: boolean;
  payment?: { mode: string; instrumentRef?: string; cashAmount?: number };
  encashment?: { declaredOnArrival: boolean; certificateRequested: boolean; payoutMethod: "cash" | "bank" };
  lines: {
    productId?: number;
    currency: string;
    fxAmount: number;
    rateOverride?: number;
    rateOverrideReason?: string;
  }[];
  delivery?: {
    line1: string;
    line2?: string;
    landmark?: string;
    city: string;
    state?: string;
    postalCode: string;
    country?: string;
  };
  trip?: { destinationCountry?: string; departureAt?: string; returnAt?: string };
  opportunityId?: string;
  /** A coupon code, checked by core when the order is placed. A sale only. */
  couponCode?: string;
  /** The delivery city's id from the catalogue; the delivery charge is worked out from it. */
  cityId?: string;
};

export type DeliveryCity = { id: string; name: string; state: string | null };

/** The cities we deliver to, for the picker: the delivery charge is worked out per city. */
export async function listServiceableCities(): Promise<DeliveryCity[]> {
  try {
    const { data } = await moneefloApi.get<DeliveryCity[]>("/catalog/cities", {
      params: { serviceableOnly: true },
    });
    return data;
  } catch (error) {
    return surface(error, "The delivery cities could not be loaded.", "forex-cities");
  }
}

/**
 * Places a sale or an encashment. The server prices it with the same rate and
 * GST functions the wizard already showed, so no total is sent. It is always
 * accepted as a draft; what would stop it being confirmed is shown on the
 * order itself.
 */
export async function createForexOrder(payload: CreateForexOrderPayload) {
  try {
    if (payload.delivery && !payload.cityId) {
      throw new Error("Pick the delivery city from the list, so the delivery charge can be worked out.");
    }
    const { data } = await moneefloApi.post<{ id: string; orderNo: string }>("/orders/forex", payload);
    return {
      // The reference staff say out loud, as the orders list shows it.
      order_id: data.orderNo.replace(/^(ORD-)?MF-/, ""),
      id: data.id,
    };
  } catch (error) {
    return surface(error, "The order could not be placed.", "forex-create-order");
  }
}
